import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  Tooltip,
  Grid,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import facturaCompraService, { FacturaCompra, FacturaCompraSearchParams } from '../../services/facturaCompraService';
import { formatCurrency, formatDate } from '../../utils/formatters';

const getFirstDayOfMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const getLastDayOfMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().split('T')[0];
};

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return '-';
  return formatDate(d.toISOString().split('T')[0]);
};

/** Formato Comprobante: A-0001-00000001 */
const formatoComprobante = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

const FacturasCompra: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [facturas, setFacturas] = useState<FacturaCompra[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    return {
      searchTerm: '',
      fechaDesde: (desde && /^\d{4}-\d{2}-\d{2}$/.test(desde)) ? desde : getFirstDayOfMonth(),
      fechaHasta: (hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta)) ? hasta : getLastDayOfMonth(),
      nroProveedor: '',
      letra: '',
      nroSuc: '',
      nroComprobante: ''
    };
  });

  const fetchFacturas = useCallback(async (
    pageNum: number = page,
    pageSize: number = rowsPerPage,
    filtros: typeof filters = filters
  ) => {
    try {
      setLoading(true);
      const params: FacturaCompraSearchParams = {
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: filtros.searchTerm || undefined,
        fechaDesde: filtros.fechaDesde ? new Date(filtros.fechaDesde) : undefined,
        fechaHasta: filtros.fechaHasta ? new Date(filtros.fechaHasta) : undefined,
        nroProveedor: filtros.nroProveedor || undefined,
        letra: filtros.letra || undefined,
        nroSuc: filtros.nroSuc ? parseInt(filtros.nroSuc, 10) : undefined,
        nroComprobante: filtros.nroComprobante ? parseInt(filtros.nroComprobante, 10) : undefined
      };
      const result = await facturaCompraService.getFacturasCompra(params);
      setFacturas(result.items || []);
      setTotalCount(result.totalCount || 0);
    } catch (error: unknown) {
      console.error('Error al cargar facturas de compra:', error);
      const msg = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
        : 'Error al cargar facturas de compra';
      enqueueSnackbar(msg || 'Error al cargar facturas de compra', { variant: 'error' });
      setFacturas([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchFacturas();
  }, []);

  // Sincronizar fechas desde URL cuando cambian los searchParams (ej. al llegar desde dashboard)
  useEffect(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    setFilters(f => ({
      ...f,
      ...(desde && /^\d{4}-\d{2}-\d{2}$/.test(desde) ? { fechaDesde: desde } : {}),
      ...(hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta) ? { fechaHasta: hasta } : {})
    }));
    setPage(0);
  }, [searchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchFacturas(0, rowsPerPage, filters);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchFacturas(newPage, rowsPerPage, filters);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchFacturas(0, newRowsPerPage, filters);
  };

  const handleBuscar = () => {
    setPage(0);
    fetchFacturas(0, rowsPerPage, filters);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Facturas de Compra
            </Typography>
            <Button
              variant="contained"
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
              startIcon={<AddIcon />}
              onClick={() => navigate('/compras/facturas/nuevo')}
            >
              Nueva Factura
            </Button>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar..."
                value={filters.searchTerm}
                onChange={(e) => setFilters(f => ({ ...f, searchTerm: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#1976d2' }} />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha desde"
                value={filters.fechaDesde}
                onChange={(e) => setFilters(f => ({ ...f, fechaDesde: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha hasta"
                value={filters.fechaHasta}
                onChange={(e) => setFilters(f => ({ ...f, fechaHasta: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Letra"
                value={filters.letra}
                onChange={(e) => setFilters(f => ({ ...f, letra: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 1 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Suc"
                value={filters.nroSuc}
                onChange={(e) => setFilters(f => ({ ...f, nroSuc: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Nro. Comp."
                value={filters.nroComprobante}
                onChange={(e) => setFilters(f => ({ ...f, nroComprobante: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="Nro. Proveedor"
                value={filters.nroProveedor}
                onChange={(e) => setFilters(f => ({ ...f, nroProveedor: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button variant="outlined" onClick={handleBuscar} sx={{ borderRadius: 2, color: '#1976d2', borderColor: '#1976d2' }}>
                Buscar
              </Button>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress sx={{ color: '#1976d2' }} />
            </Box>
          ) : isMobile ? (
            <>
              <Stack spacing={2}>
                {facturas.map((fc) => (
                  <Card key={fc.nroFormulario} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {formatoComprobante(fc.letra, fc.nroSuc, fc.nroComprobante ?? undefined)} - {fc.nombre || 'Sin nombre'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Nro. Formulario: {fc.nroFormulario} | Fecha: {formatearFecha(fc.fechaEmision)} | Total: {formatCurrency(fc.importeTotal ?? 0)}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Ver">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/compras/facturas/${fc.nroFormulario}`)}
                            sx={{ color: '#1976d2' }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
                sx={{ mt: 2 }}
              />
            </>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. Formulario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Importe Total</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturas.map((fc) => (
                    <TableRow key={fc.nroFormulario} hover>
                      <TableCell>{fc.nroFormulario}</TableCell>
                      <TableCell>{formatearFecha(fc.fechaEmision)}</TableCell>
                      <TableCell>{formatoComprobante(fc.letra, fc.nroSuc, fc.nroComprobante ?? undefined)}</TableCell>
                      <TableCell>{fc.nombre || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(fc.importeTotal ?? 0)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/compras/facturas/${fc.nroFormulario}`)}
                            sx={{ color: '#1976d2' }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FacturasCompra;
